/**
 * droid_motion — play a droid's server positions back smoothly (2026-09-24). Pure: no three, no DOM.
 *
 * The server moves a droid once a second (the scheduled droid_tick, DROID_TICK_MS) by its speed × the REAL elapsed
 * time, in a straight line toward its target, and stamps the step (`droid_vitals.lastStepAt`, written in the same
 * transaction as `droid_unit.posX/posZ`). The renderer used to CHASE each new row with `1 − 0.002^dt`, which closes
 * ~95 % of the gap in half a second: the droid sprinted to each sample, stood still for half a second, sprinted again
 * ("it jolts to a position, waits half a second, then jolts again").
 *
 * A droid is now PLAYED BACK. Each sample is kept with its SERVER stamp, and a playhead walks the polyline through
 * them at the server's own pace, about one interval behind the newest sample — a jitter buffer. Because the server
 * moved the droid by speed × the true dt between stamps, playing the stamps back reproduces its motion exactly: a
 * walking droid glides at its real speed, and it stops exactly where the server stopped it (nothing is predicted, so
 * nothing overshoots). The cost is one server interval of latency, which a commanded unit hides well.
 *
 *   · the target lag is the observed interval plus its observed JITTER (a mean absolute deviation, measured live:
 *     the droid tick arrives 0.6–2.4 s apart while the sim tick holds the module), so a steady server costs little
 *     latency and a jittery one gets the cushion it needs;
 *   · the playhead drifts toward that lag (rate 0.75–1.5×) and EASES into the newest sample (EASE_S), so a tick later
 *     than the cushion is a brief slowdown rather than a halt, and a real stop settles instead of stopping dead;
 *   · a sample after a long silence (an idle droid is not stepped) starts the move one tick before it;
 *   · a playhead left far behind (a hidden tab) catches up in one step; a jump past TELEPORT_M snaps;
 *   · a changed position with an OLDER stamp (the dev box's wall clock steps back — CLOCK-STEP LAW) is re-stamped
 *     one interval after the newest sample, so a clock step never freezes the droid.
 *
 * THE ROUTE BRIDGES A LATE TICK (droid_route, same day). A buffer alone trades latency against stalls, and the live
 * droid tick arrives 0.6–2.5 s apart while the sim tick holds the module — a short walk ended before the jitter was
 * learned and the droid eased almost to a halt at the late tick. The server publishes each walking step's STOP POINT
 * and speed, stamped like the position. When the playhead reaches the newest sample and a route stamped for that
 * sample exists, it keeps walking toward the goal at the route's speed (≤ MAX_EXTRAP_S, never past the goal). The
 * server moves the droid by speed × the real elapsed time along that same line, so the late sample lands where the
 * droid already is: the interpolation that follows is continuous. No route (a droid standing, parked, down): the
 * buffer eases in as before.
 *
 * THE STAND BEFORE A WALK (droid_pos, the gaps wave round 2, 2026-09-28). The position left the fat public unit row for
 * the narrow `droid_pos`, written ONLY by a step that moved the droid — so a droid that stood a few ticks (a `wait`, a
 * scan) sends no samples meanwhile, and the next sample would be drawn as a slow walk across the whole pause. The row
 * carries `fromAt`, when that step's walk began: `push(t, x, z, from)` inserts a sample at `from` on the last point
 * (the droid stood there until then) — exact where the GAP_S rule could only guess, and the interval is learned from
 * the step's own duration (t − from), not from the pause.
 */

export interface MotionSample { t: number; x: number; z: number }

/** A position jump beyond this is a teleport (recall, redeploy): snap, never glide across the map. */
export const TELEPORT_M = 40;
/** The server's tick (DROID_TICK_MS) in seconds — the prior for the observed sample interval. */
export const NOMINAL_S = 1;
/** The floor of the slack on top of the observed interval (the jitter term replaces it when larger). */
export const LAG_MARGIN_S = 0.25;
/** The slack in units of the observed jitter (mean absolute deviation of the sample spacing). */
export const JITTER_K = 1;
/** Within this much server time of the newest sample the playhead decelerates (never below EASE_FLOOR × its pace). */
export const EASE_S = 0.35;
export const EASE_FLOOR = 0.15;
export const MIN_LAG_S = 0.4;
export const MAX_LAG_S = 3;
/** A sample after a longer silence starts the move one tick before it (the droid stood still until then). */
export const GAP_S = 3;
/** The playhead's pace bounds while it drifts toward the target lag. */
export const RATE_MIN = 0.85;
export const RATE_MAX = 1.25;
/** How hard the pace leans on the lag error (per second of error). */
export const RATE_GAIN = 0.25;
const MAX_SAMPLES = 8;
/** The longest a route may carry the droid past its newest sample (a server that stopped stepping). */
export const MAX_EXTRAP_S = 3;

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

export class DroidTrack {
  private s: MotionSample[] = [];
  private play: number | null = null;
  /** EMA of the spacing between server samples (seconds). */
  interval = NOMINAL_S;
  /** EMA of how far a spacing strays from `interval` (seconds) — the jitter the lag must absorb. */
  jitter = 0;
  /** The rendered position (valid after the first push). */
  x = 0;
  z = 0;
  /** The direction of the segment being played (for the heading), or null when standing. */
  heading: number | null = null;
  /** Where the droid walks from its newest sample (droid_route), stamped with that sample; null when none. */
  private route: { t: number; gx: number; gz: number; speed: number } | null = null;

  /** The server's route for the step stamped `t` (call every frame; `null` clears it). */
  setRoute(r: { t: number; gx: number; gz: number; speed: number } | null): void {
    if (!r || !Number.isFinite(r.t) || !Number.isFinite(r.gx) || !Number.isFinite(r.gz) || !(r.speed > 0)) { this.route = null; return; }
    this.route = r;
  }

  /** The newest sample's stamp, or null before the first. */
  get newest(): number | null { return this.s.length ? this.s[this.s.length - 1].t : null; }

  /** How far behind the newest sample the playhead aims to run. */
  lag(): number { return clamp(this.interval + Math.max(LAG_MARGIN_S, JITTER_K * this.jitter), MIN_LAG_S, MAX_LAG_S); }

  /** Drop everything and stand at (x, z) as of stamp t. */
  reset(t: number, x: number, z: number): void {
    this.s = [{ t, x, z }];
    this.play = t;
    this.x = x;
    this.z = z;
    this.heading = null;
  }

  /**
   * Offer the row's position at its step stamp (call it every frame — an unchanged sample is ignored). `from` is when
   * that step's walk began (droid_pos.fromAt): the droid stood at the previous sample until then.
   * Returns true when the droid snapped (the first sample, or a teleport).
   */
  push(t: number, x: number, z: number, from?: number): boolean {
    if (!Number.isFinite(t) || !Number.isFinite(x) || !Number.isFinite(z)) return false;
    const last = this.s[this.s.length - 1];
    if (!last) { this.reset(t, x, z); return true; }
    const moved = Math.hypot(x - last.x, z - last.z);
    if (moved > TELEPORT_M) { this.reset(t, x, z); return true; }
    if (t <= last.t) {
      if (moved < 1e-4) return false; // the same sample, read again
      if (t === last.t) { last.x = x; last.z = z; return false; } // the same step, a later write
      t = last.t + this.interval; // an older stamp for a NEW position: the server's clock stepped back
    }
    // THE STAND BEFORE A WALK: a `from` after the last sample means the droid stood there until `from`.
    const stood = from !== undefined && Number.isFinite(from) && from > last.t + 1e-3 && from < t ? from : null;
    const gap = t - (stood ?? last.t); // the step's own duration — never the pause before it
    if (gap <= GAP_S) {
      this.jitter += (Math.abs(gap - this.interval) - this.jitter) * 0.25;
      this.interval += (gap - this.interval) * 0.25;
    }
    if (this.play !== null && this.play > last.t + 1e-9) {
      // The route carried the droid past its last sample: the DRAWN point becomes a sample, so the next segment starts
      // where the droid is on screen (a droid that reached its goal inside the late tick would otherwise jump back).
      if (t <= this.play) t = this.play + 0.05;
      this.s.push({ t: this.play, x: this.x, z: this.z });
      if (stood !== null && stood > this.play && stood < t) this.s.push({ t: stood, x: this.x, z: this.z });
      else if (stood === null && t - this.play > GAP_S) this.s.push({ t: t - NOMINAL_S, x: this.x, z: this.z });
    } else if (stood !== null) {
      this.s.push({ t: stood, x: last.x, z: last.z });
      // The playhead PARKED on the last sample while the droid stood (no samples come while it stands): that wait WAS
      // the stand. Skip it forward inside [last, from] — the droid is still there, so the jump is invisible — to where
      // the buffer wants it; else it would stand again for the whole stand and crawl to catch up (measured: 8.5 s drawn
      // for 7.0 s held, verify:droid-motion 13).
      // Only a playhead that has REACHED the last sample: one still easing toward it keeps walking there (a skip would jump).
      if (this.play !== null && Math.abs(this.play - last.t) <= 1e-6) this.play = Math.max(this.play, Math.min(stood, t - this.lag()));
    } else if (t - last.t > GAP_S) {
      // An idle droid is not stepped: it stood where it was until about one tick before this sample.
      this.s.push({ t: t - NOMINAL_S, x: last.x, z: last.z });
    }
    this.s.push({ t, x, z });
    while (this.s.length > MAX_SAMPLES) this.s.shift();
    return false;
  }

  /** Advance the playhead by a frame's dt (seconds) and return the rendered position. */
  advance(dt: number): { x: number; z: number } {
    const n = this.s.length;
    if (n === 0 || this.play === null) return { x: this.x, z: this.z };
    const newest = this.s[n - 1].t;
    const L = this.lag();
    const lead = newest - this.play;
    // A route stamped for the newest sample may carry the playhead past it (a late tick); otherwise it eases in.
    const route = this.route && Math.abs(this.route.t - newest) < 1e-3 ? this.route : null;
    if (lead > L * 3) this.play = newest - L; // left far behind (a hidden tab, a long silence): catch up at once
    else {
      const ease = route || lead >= EASE_S ? 1 : Math.max(EASE_FLOOR, lead / EASE_S); // decelerate into the newest sample
      const limit = route ? newest + MAX_EXTRAP_S : newest;
      this.play = Math.min(limit, this.play + Math.max(0, dt) * clamp(1 + (lead - L) * RATE_GAIN, RATE_MIN, RATE_MAX) * ease);
    }
    if (route && this.play > newest) {
      // Past the newest sample: walk on toward the stop point at the route's speed, never beyond it.
      const b = this.s[n - 1];
      const dx = route.gx - b.x, dz = route.gz - b.z;
      const d = Math.hypot(dx, dz);
      const go = Math.min(d, route.speed * (this.play - newest));
      this.x = d > 1e-6 ? b.x + (dx / d) * go : b.x;
      this.z = d > 1e-6 ? b.z + (dz / d) * go : b.z;
      this.heading = d - go > 0.05 ? Math.atan2(dx, dz) : null;
      while (this.s.length > 1) this.s.shift(); // everything before the newest sample is spent
      return { x: this.x, z: this.z };
    }
    // Keep one sample behind the playhead; older ones are spent.
    while (this.s.length > 2 && this.s[1].t <= this.play) this.s.shift();
    const a = this.s[0];
    if (this.play <= a.t || this.s.length === 1) {
      this.x = a.x; this.z = a.z; this.heading = null;
      return { x: this.x, z: this.z };
    }
    const b = this.s[1];
    const f = clamp((this.play - a.t) / Math.max(1e-6, b.t - a.t), 0, 1);
    this.x = a.x + (b.x - a.x) * f;
    this.z = a.z + (b.z - a.z) * f;
    const dx = b.x - a.x, dz = b.z - a.z;
    // Facing follows the segment being walked (the server's convention: yaw = atan2(dx, dz)); none while it stands.
    this.heading = f < 1 && Math.hypot(dx, dz) > 0.05 ? Math.atan2(dx, dz) : null;
    return { x: this.x, z: this.z };
  }
}
